import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const resultItems = [
  { label: '综合总分', icon: 'ri-bar-chart-fill', color: 'primary' as const, desc: '整体呈现' },
  { label: '维度分', icon: 'ri-stack-line', color: 'accent' as const, desc: '看清课堂结构' },
  { label: '分档结果', icon: 'ri-medal-line', color: 'primary' as const, desc: '识别质量层次' },
  { label: '关键证据', icon: 'ri-file-search-line', color: 'accent' as const, desc: '可回到课堂原话' },
  { label: '改进建议', icon: 'ri-lightbulb-line', color: 'primary' as const, desc: '回答"下一步怎么做"' },
  { label: '资料关联', icon: 'ri-link-m', color: 'accent' as const, desc: '权威内容补充' },
  { label: '趋势标签', icon: 'ri-price-tag-3-line', color: 'primary' as const, desc: '偶发还是反复出现' },
];

const audiences = [
  { label: '教师', icon: 'ri-user-star-line', color: 'primary' as const, use: '怎么改' },
  { label: '学生', icon: 'ri-graduation-cap-line', color: 'accent' as const, use: '怎么学' },
  { label: '专家', icon: 'ri-shield-user-line', color: 'primary' as const, use: '怎么评' },
  { label: '管理者', icon: 'ri-bar-chart-grouped-line', color: 'accent' as const, use: '怎么管' },
];

export default function P61ServiceTeacher({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-20 right-24 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-96 h-96 rounded-full bg-accent-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-6 md:py-8">
        <div ref={titleRef} className="text-center mb-4 md:mb-5">
          <span className={`inline-flex items-center gap-2 text-xs font-body font-semibold text-gold-600 uppercase tracking-[0.15em] mb-4 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="gold-dot" />一套评课结果
          </span>
          <h2 className={`font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            同时输出<span className="text-primary-600">结论</span>、<span className="text-gold-600">证据</span>和<span className="text-primary-600">行动建议</span>
          </h2>
          <p className={`text-sm text-foreground-600 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            同一套课堂事实，可以形成不同对象各取所需的应用视图
          </p>
        </div>

        {/* Central Result Package */}
        <div className={`max-w-4xl mx-auto mb-5 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="p-6 md:p-8 glass-liquid-gold rounded-2xl relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary-100/5 to-gold-100/8 pointer-events-none" />
            <div className="relative z-10">
              <div className="text-center mb-4">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass border border-gold-200/30 mb-3">
                  <span className="gold-dot" />
                  <span className="text-xs font-body font-bold text-gold-700">AI评课结果包</span>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {resultItems.map((item, i) => (
                  <div
                    key={item.label}
                    className="flex flex-col items-center gap-1.5 p-3 rounded-xl glass border border-background-200/40 text-center"
                    style={{ transitionDelay: `${300 + i * 80}ms` }}
                  >
                    <div className={`w-9 h-9 rounded-lg ${item.color === 'accent' ? 'bg-gold-100/80 border border-gold-300/40' : 'bg-primary-100/60 border border-primary-200/40'} flex items-center justify-center`}>
                      <i className={`${item.icon} text-sm ${item.color === 'accent' ? 'text-gold-600' : 'text-primary-600'}`} />
                    </div>
                    <span className="text-xs font-body font-bold text-foreground-800">{item.label}</span>
                    <span className="text-[10px] text-foreground-400 font-body leading-tight">{item.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Four Audience Views */}
        <div className={`max-w-4xl mx-auto mb-4 transition-all duration-1000 delay-500 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <p className="text-xs font-body font-semibold text-foreground-500 text-center mb-3">同一套底层数据 → 四种应用视图</p>
          <div className="flex flex-wrap items-center justify-center gap-3 md:gap-5">
            {audiences.map((a, i) => (
              <div
                key={a.label}
                className="px-5 py-3 rounded-2xl glass-liquid border border-background-200/60 flex items-center gap-2.5"
                style={{ transitionDelay: `${500 + i * 100}ms` }}
              >
                <div className={`w-9 h-9 rounded-lg ${a.color === 'accent' ? 'bg-gold-100/80 border border-gold-300/40' : 'bg-primary-100/60 border border-primary-200/40'} flex items-center justify-center`}>
                  <i className={`${a.icon} text-sm ${a.color === 'accent' ? 'text-gold-600' : 'text-primary-600'}`} />
                </div>
                <div>
                  <span className="text-sm font-body font-bold text-foreground-800">{a.label}</span>
                  <span className={`text-xs font-body ml-1.5 ${a.color === 'accent' ? 'text-gold-600' : 'text-primary-600'}`}>{a.use}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={`max-w-3xl mx-auto text-center transition-all duration-1000 delay-700 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="p-5 glass-liquid rounded-xl">
            <p className="text-sm text-foreground-600 font-body leading-relaxed">
              教师、学生、专家和管理者看到的不是四套互不关联的结论，而是<strong className="text-gold-700">同一套课堂事实</strong>经过不同视角组织后形成的应用结果。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}